import React, { useState, useEffect } from "react";
import { pageCache } from '../utils/pageCache'
import { useParams, useNavigate } from "react-router-dom";
import AppBar from "../components/AppBar";
import LoadingSpinner from "../components/LoadingSpinner";
import { userApi, SavedPlaceCategory } from "../api/user";
import { toastUtil } from "../utils/toast";
import { useCategoryStore, categoryRank } from "../stores/categoryIconStore";
import Gap from "../components/Gap";
import { useAuthStore } from "../stores/authStore";

const SavedPlaces: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { userId: currentUserId } = useAuthStore();
  const getCategoryIconBrown = useCategoryStore(
    (state) => state.getCategoryIconBrown,
  );
  const getCategoryName = useCategoryStore((state) => state.getCategoryName);
  // 스와이프로 돌아왔을 때 이전 내용을 그대로 보여준다 (깜빡임 방지)
  const cacheKey = `saved-places:${userId ?? ''}`
  const [categories, setCategories] = useState<SavedPlaceCategory[]>(
    () => pageCache.get<SavedPlaceCategory[]>(cacheKey) ?? []
  );
  const [isLoading, setIsLoading] = useState(!pageCache.has(cacheKey));
  const [isPrivate, setIsPrivate] = useState(false);
  // 상대 프로필: 나와 둘 다 저장한 곳 수 (본인이면 null)
  const [commonCount, setCommonCount] = useState<number | null>(null);

  useEffect(() => {
    if (!userId) {
      toastUtil.error("사용자 정보를 불러오는데 실패했습니다");
      navigate(-1);
      return;
    }

    const loadData = async () => {
      setIsLoading(true);
      setIsPrivate(false);
      try {
        const response = await userApi.getSavedPlacesByCategory(userId);
        /*
          이 화면만 카테고리를 **서버 순서 그대로** 그린다 — 다른 화면처럼
          categoryIconStore 를 거치지 않기 때문이다. 서버 쿼리에 ORDER BY 가
          없어서(UserController.getSavedPlacesByCategory 의 PlaceCategory::all())
          그대로 두면 여기만 카페·술집·레스토랑으로 어긋난다. 같은 기준으로
          맞춘 뒤 캐시에도 맞춘 것을 넣는다.
        */
        const ordered = [...response.categories].sort(
          (a, b) => categoryRank(a.category) - categoryRank(b.category),
        );
        pageCache.set(cacheKey, ordered);
        setCategories(ordered);
        setCommonCount(response.common_count ?? null);
      } catch (error: any) {
        // 403 에러인 경우 비공개 계정으로 처리
        if (error.response?.status === 403) {
          setIsPrivate(true);
          setCategories([]);
        } else {
          const errorMessage =
            error.response?.data?.message ||
            "저장한 장소를 불러오는데 실패했습니다";
          toastUtil.error(errorMessage);
          navigate(-1);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [userId, navigate]);

  const handleCategoryClick = (category: string) => {
    navigate(`/user/${userId}/saved-places/${category}`);
  };

  const handleCommonClick = () => {
    navigate(`/user/${userId}/saved-places/common`);
  };

  const isMine = currentUserId === userId;
  // 상대 프로필이면 "우리 둘 다 저장한 곳"을 항상 노출(0곳이어도).
  // commonCount는 본인 프로필일 때만 null이라 사실상 상대 프로필 판별과 동일.
  const showCommon = !isMine && commonCount != null;

  const displayPrivateBox = () => {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center px-16px pointer-events-none empty-state-center">
        <img
          src="/images/lock-icon.png"
          alt="비공개"
          className="w-96px h-96px object-contain"
        />

        <Gap height={16} />

        <div className="font-pretendard font-regular text-16px leading-180% text-textColorText3 text-center">
          비공개 계정입니다.
          <br />
          팔로우 승인 후 열람 가능합니다.
        </div>
      </div>
    );
  };

  const renderCategoryItem = (category: SavedPlaceCategory) => {
    const categoryCode = category.category;
    const iconPath = getCategoryIconBrown(categoryCode);
    const categoryName = getCategoryName(categoryCode) || category.name;

    return (
      <div
        key={category.category}
        className="w-full flex items-center px-20px py-16px cursor-pointer border-b border-472410-20%"
        onClick={() => handleCategoryClick(categoryCode)}
      >
        {/* 아이콘 — 레스토랑 아이콘만 살짝 크게 보여 27px로(비율 유지), 나머지는 28px */}
        {iconPath && (
          <img
            src={iconPath}
            alt={categoryName}
            className={`object-contain ${
              categoryCode === "restaurant" ? "w-[27px] h-[27px]" : "w-28px h-28px"
            }`}
          />
        )}

        <Gap width={12} />

        {/* 카테고리 이름 */}
        <div className="flex-1 min-w-0 truncate font-pretendard font-bold text-16px leading-125% text-brown-80%-main">
          {categoryName}
        </div>

        <Gap width={8} />

        {/* 개수 */}
        <div className="font-pretendard font-bold text-18px leading-125% text-brown-80%-main">
          {category.count}
        </div>

        <Gap width={8} />

        {/* 화살표 */}
        <img
          src="/images/bold-arrow-right-icon.png"
          alt="이동"
          className="w-24px h-24px object-contain"
        />
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
        <div className="app-container flex-1 flex flex-col overflow-hidden">
          <AppBar title="저장한 장소" isBottomBorder />
          <div className="flex-1 flex items-center justify-center empty-state-center">
            <LoadingSpinner size={28} borderWidth={2} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden relative">
        {/* 헤더 */}
        <AppBar title="저장한 장소" isBottomBorder />

        {/* 카테고리 목록 */}
        <div className="flex-1 overflow-y-auto scrollbar-hide relative">
          {isPrivate && !isMine ? (
            displayPrivateBox()
          ) : categories.length > 0 || showCommon ? (
            <>
              {categories.map((category) => renderCategoryItem(category))}
              {/* 우리 둘 다 저장한 곳 (상대와의 저장 교집합) */}
              {showCommon && (
                <div
                  className="w-full flex items-center px-20px py-16px cursor-pointer border-b border-472410-20%"
                  onClick={handleCommonClick}
                >
                  {/* 28px 슬롯(정렬 유지) 안에서 아이콘만 21px로 → 카테고리 아이콘과 시각 크기 통일.
                      '둘 다 저장한 곳' 전용 북마크 아이콘(첨부). opacity 80%는 PNG 알파에 내장(204/255)
                      → CSS opacity를 또 주면 이중(0.64)이 되므로 주지 않는다. */}
                  <div className="w-28px h-28px flex items-center justify-center">
                    <img
                      src="/images/both-saved-bookmark.png"
                      alt="우리 둘 다 저장한 곳"
                      className="w-[23px] h-[23px] object-contain"
                    />
                  </div>
                  <Gap width={12} />
                  <div className="flex-1 font-pretendard font-bold text-16px leading-125% text-brown-80%-main">
                    우리 둘 다 저장한 곳
                  </div>
                  <Gap width={8} />
                  <div className="font-pretendard font-bold text-18px leading-125% text-brown-80%-main">
                    {commonCount}
                  </div>
                  <Gap width={8} />
                  <img
                    src="/images/bold-arrow-right-icon.png"
                    alt="이동"
                    className="w-24px h-24px object-contain"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center px-16px pointer-events-none empty-state-center">
              <p className="font-pretendard font-regular text-16px leading-180% text-textColorText3 text-center">
                저장한 장소가 없습니다
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavedPlaces;
